import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  IconButton,
  Chip,
  Rating,
  CircularProgress
} from '@mui/material';
import { Menu as MenuIcon, Search as SearchIcon, Person as PersonIcon, ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Product, Category, BlogPost, Testimonial } from '../../services/FirestoreService';
import { getFeaturedProducts, getCategories, getBlogPosts, getSliders, getTestimonials, getSiteSettings } from '../../services/FirestoreService';

interface SliderItem {
  id: string;
  title: string;
  image: string;
  category: string;
  categoryId: string;
  isActive: boolean;
}

const Homepage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [sliders, setSliders] = useState<SliderItem[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [siteSettings, setSiteSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Slick slider settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  const categorySliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 5,
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 4,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 2,
        }
      }
    ]
  };

  const productSliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [fetchedProducts, fetchedCategories, fetchedBlogPosts, fetchedSliders, fetchedTestimonials, fetchedSiteSettings] = await Promise.all([
          getFeaturedProducts(6),
          getCategories(),
          getBlogPosts(),
          getSliders(),
          getTestimonials(),
          getSiteSettings()
        ]);
        
        setProducts(fetchedProducts);
        setCategories(fetchedCategories);
        setBlogPosts(fetchedBlogPosts.slice(0, 3)); // Get only first 3 blog posts
        setSliders(fetchedSliders.filter(slider => slider.isActive)); // Only show active sliders
        setTestimonials(fetchedTestimonials);
        setSiteSettings(fetchedSiteSettings);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <CircularProgress />
      </Container>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Menu */}
      <div className="bg-gray-100 py-3 px-4 hidden md:block">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <IconButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="h1" className="text-xl md:text-2xl">
              Rölyef Boyama Setleri
            </Typography>
          </div>
          <div className="flex items-center space-x-4">
            <IconButton component={Link} to="/search">
              <SearchIcon />
            </IconButton>
            <IconButton component={Link} to="/profile">
              <PersonIcon />
            </IconButton>
            <IconButton component={Link} to="/cart">
              <ShoppingCartIcon />
            </IconButton>
          </div>
        </div>
      </div>

      {/* Slider */}
      <div className="mb-8">
        <Slider {...sliderSettings}>
          {sliders.map((slider) => (
            <div key={slider.id} className="relative">
              <img 
                src={slider.image} 
                alt={slider.title} 
                className="w-full h-64 md:h-96 object-cover" 
              />
              <div 
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
                          text-white text-center bg-black bg-opacity-50 p-6 rounded-lg"
              >
                <Typography variant="h4" component="h2" className="text-white">
                  {slider.title}
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  className="mt-4"
                  component={Link}
                  to={`/category/${slider.categoryId}`}
                >
                  Daha Fazla
                </Button>
              </div>
            </div>
          ))}
        </Slider>
      </div>

      {/* Widgets: Secure Payment, Free Shipping */}
      <div className="container mx-auto py-8 px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="text-center py-6">
            <CardContent>
              <Typography variant="h6" color="primary">Güvenli Ödeme</Typography>
              <Typography variant="body2">SSL Güvencesi</Typography>
            </CardContent>
          </Card>
          <Card className="text-center py-6">
            <CardContent>
              <Typography variant="h6" color="primary">Ücretsiz Kargo</Typography>
              <Typography variant="body2">{siteSettings?.freeShippingThreshold || 750} TL ve Üzeri</Typography>
            </CardContent>
          </Card>
          <Card className="text-center py-6">
            <CardContent>
              <Typography variant="h6" color="primary">Hızlı Teslimat</Typography>
              <Typography variant="body2">48 Saatte Kapınızda</Typography>
            </CardContent>
          </Card>
          <Card className="text-center py-6">
            <CardContent>
              <Typography variant="h6" color="primary">Kolay İade</Typography>
              <Typography variant="body2">14 Günlük İade Hakkı</Typography>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Categories Slider */}
      <div className="container mx-auto py-8 px-4">
        <Typography variant="h5" component="h2" className="text-center mb-6 text-xl md:text-2xl">
          Kategoriler
        </Typography>
        <Slider {...categorySliderSettings}>
          {categories.map((category) => (
            <div key={category.id} className="px-2">
              <Card 
                component={Link} 
                to={`/category/${category.id}`}
                className="no-underline transition-transform duration-300 hover:scale-105"
              >
                <CardMedia
                  component="img"
                  height="140"
                  image={category.image}
                  alt={category.name}
                />
                <CardContent>
                  <Typography variant="body2" align="center" className="text-sm">
                    {category.name}
                  </Typography>
                </CardContent>
              </Card>
            </div>
          ))}
        </Slider>
      </div>

      {/* Customer Testimonials */}
      <div className="container mx-auto py-8 px-4">
        <Typography variant="h5" component="h2" className="text-center mb-6 text-xl md:text-2xl">
          Müşteri Yorumları
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.slice(0, 3).map((testimonial) => (
            <Card key={testimonial.id}>
              <CardContent>
                <Rating value={testimonial.rating} readOnly />
                <Typography variant="body1" className="mt-2">
                  "{testimonial.comment}"
                </Typography>
                <Typography variant="body2" color="textSecondary" className="mt-2">
                  - {testimonial.name}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Best Selling Products */}
      <div className="container mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-6">
          <Typography variant="h5" component="h2" className="text-xl md:text-2xl">
            Çıkan Ürünler
          </Typography>
          <Button component={Link} to="/products" variant="text" color="primary">
            Tümünü Gör
          </Button>
        </div>
        <Slider {...productSliderSettings}>
          {products.map((product) => (
            <div key={product.id} className="px-2">
              <Card 
                component={Link} 
                to={`/product/${product.id}`}
                className="h-full flex flex-col transition-transform duration-300 hover:scale-103 no-underline"
              >
                <div className="relative">
                  <CardMedia
                    component="img"
                    height="200"
                    image={product.image}
                    alt={product.name}
                  />
                  {product.discount && product.discount > 0 && (
                    <Chip
                      label={`${product.discount}%`}
                      color="error"
                      size="small"
                      className="absolute top-2 right-2 rounded-sm"
                    />
                  )}
                  {product.isNew && (
                    <Chip
                      label="Yeni"
                      color="success"
                      size="small"
                      className="absolute top-2 left-2 rounded-sm"
                    />
                  )}
                </div>
                <CardContent className="flex-grow">
                  <Typography variant="body2" component="div" className="text-sm">
                    {product.name}
                  </Typography>
                  <Rating value={product.rating || 0} readOnly size="small" className="mt-2" />
                  <div className="mt-2 flex items-center">
                    {product.discountedPrice ? (
                      <>
                        <Typography variant="body1" color="error">
                          ₺{product.discountedPrice}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" className="ml-2 line-through">
                          ₺{product.price}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body1" color="error">
                        ₺{product.price}
                      </Typography>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </Slider>
      </div>

      {/* Admin Configurable Products */}
      <div className="container mx-auto py-8 px-4">
        <Typography variant="h5" component="h2" className="text-center mb-6 text-xl md:text-2xl">
          Öne Çıkan Ürünler
        </Typography>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.slice(0, 4).map((product) => (
            <Card 
              key={product.id}
              component={Link} 
              to={`/product/${product.id}`}
              className="no-underline h-full flex flex-col transition-transform duration-300 hover:scale-103"
            >
              <div className="relative">
                <CardMedia
                  component="img"
                  height="200"
                  image={product.image}
                  alt={product.name}
                />
                {product.discount && product.discount > 0 && (
                  <Chip
                    label={`${product.discount}%`}
                    color="error"
                    size="small"
                    className="absolute top-2 right-2 rounded-sm"
                  />
                )}
                {product.isNew && (
                  <Chip
                    label="Yeni"
                    color="success"
                    size="small"
                    className="absolute top-2 left-2 rounded-sm"
                  />
                )}
              </div>
              <CardContent className="flex-grow">
                <Typography variant="body2" component="div" className="text-sm">
                  {product.name}
                </Typography>
                <Rating value={product.rating || 0} readOnly size="small" className="mt-2" />
                <div className="mt-2 flex items-center">
                  {product.discountedPrice ? (
                    <>
                      <Typography variant="body1" color="error">
                        ₺{product.discountedPrice}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" className="ml-2 line-through">
                        ₺{product.price}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body1" color="error">
                      ₺{product.price}
                    </Typography>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Blog Posts */}
      <div className="container mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-6">
          <Typography variant="h5" component="h2" className="text-xl md:text-2xl">
            Blog
          </Typography>
          <Button component={Link} to="/blog" variant="text" color="primary">
            Tüm Yazılar
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogPosts.map((post) => (
            <Card 
              key={post.id}
              component={Link} 
              to={`/blog/${post.id}`}
              className="no-underline h-full flex flex-col transition-transform duration-300 hover:scale-103"
            >
              <CardMedia
                component="img"
                height="140"
                image={post.image}
                alt={post.title}
              />
              <CardContent className="flex-grow">
                <Typography variant="h6" component="div">
                  {post.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" className="mt-2">
                  {post.excerpt}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Homepage;